import React, { useCallback, useEffect, useRef, useState } from 'react'
import * as api from 'api'
import { Alert, Filter, GoogleMap } from 'components'
import { useSelector } from 'react-redux'
import { Encrypt } from 'helpers'
import s from './styles.module.scss'

const mapFilter = [
  { field: 'All' },
  { field: 'Businesses' },
  { field: 'Users' },
  { field: 'Business Near Me' },
  { field: 'Users Near Me' }
]

function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371 // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export default function LocationsMap({ onCloseModal }) {
  const [loading, setLoading] = useState(false)
  const [userBusinessLocations, setUserBusinessLocations] = useState([])
  const [usersLocations, setUsersLocations] = useState([])
  const [mapLocations, setMapLocations] = useState([])
  const [nearbyList, setNearbyList] = useState([])
  const [defaultCoordinates, setDefaultCoordinates] = useState(null)
  const location = useSelector(state => state.location).location
  const [selectedFilterField, setSelectedFilterField] = useState(mapFilter[0].field)
  const processing = useRef(false)
  const processing1 = useRef(false)

  const filterHandler = field => {
    setSelectedFilterField(field)
    if (field === 'Businesses') setMapLocations([...userBusinessLocations])
    if (field === 'Users') setMapLocations([...usersLocations])
    if (field === 'All') setMapLocations([...userBusinessLocations, ...usersLocations])

    if (field === 'Business Near Me' || field === 'Users Near Me') {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(pos => {
          const lat = pos.coords.latitude
          const lng = pos.coords.longitude

          const all = [...userBusinessLocations, ...usersLocations]
          const filtered = all
            .map(loc => {
              if (loc?.location?.coordinates) {
                const [locLng, locLat] = loc.location.coordinates
                const dist = haversineDistance(lat, lng, locLat, locLng)
                if (dist <= 20) {
                  return {
                    ...loc,
                    distanceKm: dist,
                    mapLink: `https://www.google.com/maps/search/?api=1&query=${locLat},${locLng}`
                  }
                }
              }
              return null
            })
            .filter(Boolean)

          const filteredByType = field === 'Business Near Me'
            ? filtered.filter(item => !item.isUser)
            : filtered.filter(item => item.isUser)

          setMapLocations(filteredByType)
          setNearbyList(filteredByType)
        })
      } else {
        Alert.error('Geolocation not supported')
      }
    }
  }

  const getLocations = useCallback(async () => {
    if (processing.current) return
    processing.current = true
    setLoading(true)

    const fetchBusinessLocations = await api.publicApi.business.fetchBusinessLocations({})
    let locationsFetched = []

    if (fetchBusinessLocations.code === 200) {
      const newBusinessLocations = fetchBusinessLocations.payload.getBusinessLocations.map(business => ({
        ...business,
        isUser: false,
        link: '/' + business.city + '/' + Encrypt({ fkBusinessId: business._id })
      }))
      setUserBusinessLocations(newBusinessLocations)
      locationsFetched = [...locationsFetched, ...newBusinessLocations]
    } else {
      Alert.error(fetchBusinessLocations.message)
    }

    const fetchUsersLocations = await api.publicApi.user.fetchUsersLocations({})
    if (fetchUsersLocations.code === 200) {
      const newUserLocations = fetchUsersLocations.payload.getUsersLocations.map(users => ({
        ...users,
        isUser: true,
        subCategory: '',
        businessName: users.firstName + ' ' + users.lastName,
        images: [users.logo],
        link: '/userProfile/' + Encrypt({ fkUserId: users._id })
      }))
      setUsersLocations(newUserLocations)
      locationsFetched = [...locationsFetched, ...newUserLocations]
    } else {
      Alert.error(fetchUsersLocations.message)
    }

    setMapLocations(locationsFetched)
    setLoading(false)
    processing.current = false
  }, [])

  useEffect(() => {
    getLocations()
  }, [getLocations])

  const getCoordinatesViaCity = useCallback(async () => {
    if (processing1.current) return
    processing1.current = true
    setLoading(true)

    const matchingCityCoordinates = await api.publicApi.locations.fetchCoordinatesViaCity({ city: location })
    if (matchingCityCoordinates.code === 200) {
      setDefaultCoordinates(matchingCityCoordinates.payload)
    } else {
      Alert.error(matchingCityCoordinates.message)
    }

    setLoading(false)
    processing1.current = false
  }, [location])

  useEffect(() => {
    if (location !== 'All Over India') getCoordinatesViaCity()
    else setDefaultCoordinates(null)
  }, [location, getCoordinatesViaCity])

  return (
    <div className={s.main}>
      <div className={s.header}>
        <h2>BCN Connect Map</h2>
        <button className={s.closeButton} onClick={onCloseModal}>
          <span className='material-icons-outlined'>close</span>
        </button>
      </div>
      
      <div className={s.content}>
        {!loading && (
          <div className={s.locationsOuter}>
            <div className={s.Locations}>
              <div className={s.title}>Explore and connect with registered BCN members near you</div>

              <div className={s.counts}>
                {selectedFilterField === 'All' && (
                  <>
                    <span>Businesses: {userBusinessLocations.length}</span>
                    <span>Users: {usersLocations.length}</span>
                  </>
                )}
                {selectedFilterField === 'Users' && (
                  <span>Users: {usersLocations.length}</span>
                )}
                {selectedFilterField === 'Businesses' && (
                  <span>Businesses: {userBusinessLocations.length}</span>
                )}
                {(selectedFilterField === 'Business Near Me' || selectedFilterField === 'Users Near Me') && (
                  <>
                    <span>Nearby: {nearbyList.length}</span>
                    <span>
                      Users: {nearbyList.filter(item => item.isUser).length}
                    </span>
                    <span>
                      Businesses: {nearbyList.filter(item => !item.isUser).length}
                    </span>
                  </>
                )}
              </div>

              <div className={s.filter}>
                <Filter
                  title={selectedFilterField}
                  heading={selectedFilterField}
                  filterFields={mapFilter}
                  filterHandler={filterHandler}
                  style2
                />
              </div>

              <div className={s.locationsContent}>
                <div className={s.locationsBox}>
                  <GoogleMap.MultipleLocations
                    defaultLocation={defaultCoordinates}
                    userBusinessLocations={mapLocations}
                  />
                </div>

                {(selectedFilterField === 'Business Near Me' || selectedFilterField === 'Users Near Me') && (
                  <div className={s.nearbyList}>
                    <h2>Nearby {selectedFilterField === 'Users Near Me' ? 'Users' : 'Businesses'}</h2>
                    {nearbyList.length === 0 ? (
                      <p>No nearby records found.</p>
                    ) : (
                      <ul>
                        {nearbyList.map((loc, idx) => (
                          <li key={idx} className={s.businessItem}>
                            <a href={loc.link} target="_blank" rel="noreferrer" className={s.businessName}>
                              {loc.businessName || loc.businessTitle} ({loc.isUser ? 'User' : 'Business'})
                            </a>
                            <p><strong>City:</strong> {loc.city || 'Unknown'}</p>
                            <p><strong>Distance:</strong> {loc.distanceKm?.toFixed(2) || '0.00'} km</p>
                            <div className={s.actions}>
                              {loc.phoneNo && (
                                <a href={`tel:${loc.phoneNo}`} className={s.callBtn}>📞 Call</a>
                              )}
                              {loc.phoneNo && (
                                <a href={`https://wa.me/${loc.phoneNo}`} target="_blank" rel="noreferrer" className={s.waBtn}>💬 WhatsApp</a>
                              )}
                              {loc.mapLink && (
                                <a href={loc.mapLink} target="_blank" rel="noreferrer" className={s.directionsBtn}>🧭 Directions</a>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {loading && (
          <div className={s.loader}>
            <div className={s.spinner}></div>
            <p>Loading locations...</p>
          </div>
        )}
      </div>
    </div>
  )
}
